import React from 'react';
import { Spinner } from './Spinner';

/**
 * Badge
 *
 * Generic colored badge for labels, group names, counts.
 *
 * Variants: default | success | warning | error | info | orange | navy
 *
 * Usage:
 *   <Badge variant="success">Active</Badge>
 *   <Badge variant="warning">Pending</Badge>
 *   <Badge dot>3 pending</Badge>
 */
export function Badge({
  variant = 'default',
  dot = false,
  className = '',
  children,
}) {
  const variants = {
    default: 'bg-[var(--color-surface-alt)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
    success: 'bg-[var(--color-success-bg)] text-[var(--color-success)] border-[var(--color-success-border)]',
    warning: 'bg-[var(--color-warning-bg)] text-[var(--color-warning)] border-[var(--color-warning-border)]',
    error:   'bg-[var(--color-error-bg)] text-[var(--color-error)] border-[var(--color-error-border)]',
    info:    'bg-[var(--color-info-bg)] text-[var(--color-info)] border-[var(--color-info-border)]',
    orange:  'bg-[var(--color-orange-light)] text-[var(--color-orange)] border-[var(--color-orange-muted)]',
    navy:    'bg-[var(--color-navy)] text-white border-[var(--color-navy)]',
  };

  return (
    <span className={[
      'inline-flex items-center gap-1.5',
      'px-2 py-0.5 rounded-[var(--radius-full)]',
      'text-[11px] font-semibold border',
      'whitespace-nowrap',
      variants[variant],
      className,
    ].filter(Boolean).join(' ')}>
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />}
      {children}
    </span>
  );
}

/**
 * StatusPill
 *
 * For fulfillment record statuses. Maps status string to CSS token colors.
 */
const STATUS_MAP = {
  'Pending':    { bg: 'var(--status-pending-bg)',    text: 'var(--status-pending-text)',    border: 'var(--status-pending-border)' },
  'Sent':       { bg: 'var(--status-sent-bg)',       text: 'var(--status-sent-text)',       border: 'var(--status-sent-border)' },
  'Boxed':      { bg: 'var(--status-boxed-bg)',      text: 'var(--status-boxed-text)',      border: 'var(--status-boxed-border)' },
  'Hold':       { bg: 'var(--status-hold-bg)',       text: 'var(--status-hold-text)',       border: 'var(--status-hold-border)' },
  'Cancelled':  { bg: 'var(--status-cancelled-bg)',  text: 'var(--status-cancelled-text)',  border: 'var(--status-cancelled-border)' },
  'Data Check': { bg: 'var(--status-datacheck-bg)',  text: 'var(--status-datacheck-text)',  border: 'var(--status-datacheck-border)' },
};

export function StatusPill({ status, className = '' }) {
  const colors = STATUS_MAP[status] || {
    bg: 'var(--color-surface-alt)',
    text: 'var(--color-text-muted)',
    border: 'var(--color-border)',
  };

  return (
    <span
      className={[
        'inline-flex items-center gap-1',
        'px-2 py-0.5 rounded-[var(--radius-full)]',
        'text-[11px] font-semibold border whitespace-nowrap',
        className,
      ].filter(Boolean).join(' ')}
      style={{ background: colors.bg, color: colors.text, borderColor: colors.border }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colors.text }} />
      {status}
    </span>
  );
}

/**
 * JobStatusBadge
 *
 * For ID matching job statuses: processing | complete | failed
 */
function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 shrink-0">
      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 shrink-0">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

const JOB_STATUS_MAP = {
  processing: { label: 'Processing', icon: <Spinner size="sm" />, variant: 'warning' },
  complete:   { label: 'Complete',   icon: <CheckIcon />,          variant: 'success' },
  failed:     { label: 'Failed',     icon: <XIcon />,              variant: 'error'   },
};

export function JobStatusBadge({ status, className = '' }) {
  const config = JOB_STATUS_MAP[status] || JOB_STATUS_MAP.processing;
  return (
    <Badge variant={config.variant} className={className}>
      {config.icon}
      {config.label}
    </Badge>
  );
}

/**
 * GroupBadge
 *
 * Colored badge for Cognito group names.
 */
export function GroupBadge({ group, groupLabels = {}, groupColors = {}, className = '' }) {
  const label      = groupLabels[group] || group;
  const colorClass = groupColors[group] || 'bg-[var(--color-surface-alt)] text-[var(--color-text-secondary)] border-[var(--color-border)]';

  return (
    <span className={[
      'inline-flex items-center px-2 py-0.5',
      'rounded-[var(--radius-full)] text-[11px] font-semibold border',
      colorClass,
      className,
    ].filter(Boolean).join(' ')}>
      {label}
    </span>
  );
}

/**
 * AlertBanner
 *
 * Inline status message for forms and pages.
 */
export function AlertBanner({ type = 'info', children, className = '' }) {
  if (!children) return null;

  const styles = {
    success: 'bg-[var(--color-success-bg)] border-[var(--color-success-border)] text-[var(--color-success)]',
    error:   'bg-[var(--color-error-bg)] border-[var(--color-error-border)] text-[var(--color-error)]',
    warning: 'bg-[var(--color-warning-bg)] border-[var(--color-warning-border)] text-[var(--color-warning)]',
    info:    'bg-[var(--color-info-bg)] border-[var(--color-info-border)] text-[var(--color-info)]',
  };

  return (
    <div className={[
      'px-3.5 py-2.5 rounded-[var(--radius-lg)] border text-xs leading-relaxed',
      styles[type],
      className,
    ].join(' ')}>
      {children}
    </div>
  );
}

export default Badge;
